/* eslint-disable @typescript-eslint/no-explicit-any */
import { CellValueChangedEvent } from '@ag-grid-community/core'
import { HttpParams } from '@angular/common/http'
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { FormControl, ValidationErrors } from '@angular/forms'
import {
  RootState,
  selectActiveSession,
  selectActiveSessionIdentifier,
  selectActiveSessionReconDate,
  selectActiveUserId,
  selectActiveWindowDate,
  selectIsHplAccount,
  selectLoadedForNonDefaultBasis,
  setCommandsMetrics,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { ToastService } from '@northfork/ng-basics'
import { compareAsc, isAfter, max, parseISO } from 'date-fns'
import { Subject } from 'rxjs'
import { debounceTime, retry, take, takeUntil } from 'rxjs/operators'
import { Basis, commonBasisTypes } from '../../../model/mvt-basis.model'
import { Security } from '../../../model/security.model'
import { AccountDetails } from '../../../model/single-transaction/account-details.model'
import { CreateEditTransactionRequest } from '../../../model/single-transaction/create-edit-transaction.model'
import { ExternalUnmatchedTransaction } from '../../../model/single-transaction/external-unmatched-transaction'
import {
  ExternalMvt,
  MvtData,
} from '../../../model/single-transaction/market-value-transfer.model'
import { TransactionModalInput } from '../../../model/single-transaction/popup-input.model'
import {
  BasisGridEdit,
  BasisGridExternal,
  GenericGridTransaction,
  SecurityGrid,
  SecurityTransfer,
} from '../../../model/single-transaction/security-transfer/security-transfer.model'
import { TransferOptions } from '../../../model/single-transaction/security-transfer/transfer-options.model'
import { TransferPurchaseInfo } from '../../../model/single-transaction/security-transfer/transfer-purchase-info.model'
import { SleeveSimpleAccounts } from '../../../model/single-transaction/sleeve-simple-accounts.model'
import { TransactionTypeLabel } from '../../../model/single-transaction/transaction'
import {
  SecurityTransferValidationView,
  ValidationBasisData,
  ValidationExternalTransactionData,
} from '../../../model/single-transaction/validation-view.model'
import {
  TranCategory,
  TranCategoryUrl,
} from '../../../model/transaction-category.model'
import {
  ENTRIES,
  TransactionTypeEnum,
  TransactionTypeKey,
} from '../../../model/transaction-type.model'
import {
  ErrorSeverity,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import { ModalFocusService } from '../../../services/modal-focus.service'
import { Calculators } from '../../../util/calculators.util'
import { getLoanFieldValues } from '../../../util/loan-field.util'
import {
  activateDialog,
  calculateBasisPrice,
  createMetadataSecurityTransfer,
  getDefaultUnknownCounterParty,
  getFieldValidatorFormatter,
  getStringISOForDate,
  isDialogComponentInActive,
  isSecurityNonCommission,
  SINGLE_DIALOG_EVENT_NAME,
  SINGLE_DIALOG_VALIDATION_EVENT_NAME,
  singleDialogMetricsDetail,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import { getDefaultCurrency } from '../../../util/currency-utils'
import {
  isEditModal,
  isExternalTransaction,
} from '../../../util/popup-input-util'
import { getRequestModeFromModalMode } from '../../../util/validation-response-util'
import { RequestPriceComponent } from './request-price/request-price.component'
import { SecurityTransferBasisGridComponent } from './security-transfer-basis/security-transfer-basis-grid.component'
import { SecurityTransferGenericGridComponent } from './security-transfer-generic/security-transfer-generic-grid.component'
import { SecurityTransferSaveConfirmationModalComponent } from './security-transfer-modal/security-transfer-confirm-button-modal'
@Component({
  selector: 'hs-security-transfer',
  templateUrl: './security-transfer.component.html',
  styleUrls: ['./security-transfer.component.scss'],
})
export class SecurityTransferModalComponent implements OnInit, OnDestroy {
  // True while an aggregate account is selected but no concrete sub-account has been
  // picked yet. Passed to the child grids so they defer validation and flag edited fields.
  aggregateAccountUnresolved = false
  @Input() transInput?: TransactionModalInput

  @ViewChild('basis')
  readonly basisComponent?: SecurityTransferBasisGridComponent
  @ViewChild('generic')
  readonly genericComponent?: SecurityTransferGenericGridComponent
  accountId?: number
  basisData?: SecurityGrid | BasisGridEdit | BasisGridExternal
  genericData?: GenericGridTransaction | SecurityGrid
  userId?: number
  reconDate?: Date
  windowDate?: Date
  sessionId?: string
  forbiddenErrors = new FormControl('')
  clearTransaction = new FormControl(false)
  reprocessTransfers = new FormControl(false)
  formSubmitting = false
  isDataValid = false
  loadedForNonDefaultBasis = false

  isCellEditing = false
  isValidating = false
  selectedTypeofTransfer = TransactionConstants.TRANSFER_OPTIONS[0]
  lastUsedSettleDate?: string
  isCollateralBrokerAccount?: boolean

  defaultBasisId = 1
  originalTransaction?: SecurityTransfer
  currentSecurityId: number = getDefaultCurrency().cashSecurityId
  security?: Security
  externalTransaction?: ExternalUnmatchedTransaction
  settleDate?: string
  entryDate?: string
  externalAccountId?: number
  isExternalAccountSleeveSimple = false
  originalCusip?: string
  executionId?: number
  accountDetails?: AccountDetails
  externalTranTypeLabel?: TransactionTypeLabel
  getFieldValidatorFormatter = getFieldValidatorFormatter
  noteWarning: ValidationErrors | null = null
  genericWarnings = new Set<string>()
  saveButtonWarning: string | null = null
  note = new FormControl('')
  sleeveSimpleAccount: SleeveSimpleAccounts[] = []
  isEdit = false
  mvtDataMap?: { [key: number]: MvtData }
  tpiMap?: { [key: number]: TransferPurchaseInfo }
  genericLoaded = false
  basisLoaded = false
  initialLoadDone = false
  compId = 'securityTransfer-' + Math.random()
  activeInstances: NgbModalRef[] = []
  errorDetails: unknown
  errorText = ''
  errorHeader: string | undefined
  title = 'Create Security Transfer'
  applyHPL = false
  private readonly destroySubject$ = new Subject()
  isCalculatingFactor = false
  origSettleDateValidationError = false

  constructor(
    public toastService: ToastService,
    readonly activeModal: NgbActiveModal,
    readonly store: Store<RootState>,
    readonly createEditTransactionService: CreateEditTransactionService,
    private readonly modalService: NgbModal,
    private readonly elRef: ElementRef,
    private readonly telemetryService: TelemetryService,
    private readonly modalFocusService: ModalFocusService,
  ) {
    this.modalService.activeInstances
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(activeInstances => {
        this.activeInstances = activeInstances
      })
    this.store
      .select(selectActiveSessionIdentifier)
      .pipe(take(1))
      .subscribe((sessionId?: string) => {
        this.sessionId = sessionId
      })

    this.store
      .select(selectIsHplAccount)
      .pipe(take(1))
      .subscribe(applyHPL => {
        this.applyHPL = applyHPL
      })
    this.store
      .select(selectActiveWindowDate)
      .pipe(take(1))
      .subscribe(windowDate => {
        this.windowDate = windowDate
      })

    this.store
      .select(selectLoadedForNonDefaultBasis)
      .pipe(take(1))
      .subscribe(loadedForNonDefaultBasis => {
        this.loadedForNonDefaultBasis = loadedForNonDefaultBasis ?? false
      })
    this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .subscribe(session => {
        this.accountId = session?.loadedAccountIds[0]
      })

    this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1))
      .subscribe((reconDate?: Date) => {
        this.reconDate = reconDate
      })
    this.store
      .select(selectActiveUserId)
      .pipe(take(1))
      .subscribe(userId => {
        this.userId = userId
      })
  }

  async ngOnInit() {
    this.isEdit = isEditModal(this.transInput)
    const startTime = Date.now()
    activateDialog(this.activeInstances, this.compId, this.elRef)

    this.modalFocusService
      .outsideFocus(this.elRef)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.disableGridFocus())

    this.note.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.validate()
    })
    this.clearTransaction.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.validate()
    })

    if (
      this.isEdit &&
      this.transInput?.transactionId &&
      this.sessionId &&
      this.accountId &&
      this.transInput?.securityId
    ) {
      this.title = this.transInput?.readOnly
        ? 'View Security Transfer'
        : 'Edit Security Transfer'
      const params = new HttpParams()
        .set('accountId', this.accountId.toString())
        .set('securityId', this.transInput?.securityId.toString())
      this.createEditTransactionService
        .loadTransactionDetail(
          this.sessionId,
          this.transInput?.transactionId,
          params,
          TranCategoryUrl.TRANSFER,
        )
        .subscribe(
          async trans => {
            this.note.setValue(trans.note?.note ?? '')
            this.accountDetails = trans.accountDetails

            this.originalTransaction = trans.transactionData as SecurityTransfer
            if (trans.accountDetails?.isAggregate) {
              await this.loadAndMapAccountDetailsForSleeve(
                this.originalTransaction.accountId,
              )
            }
            this.defaultBasisId = this.accountDetails?.defaultBasis ?? 1
            this.clearTransaction.patchValue(
              this.originalTransaction.clearedDate ? true : false,
            )

            if (trans.mvtDataMap && Object.values(trans.mvtDataMap).length > 0)
              this.mvtDataMap = trans.mvtDataMap
            if (trans.tpiDataMap && Object.values(trans.tpiDataMap).length > 0)
              this.tpiMap = trans.tpiDataMap
            const security = {
              securityMasterData: trans.securityMasterData,
              securityTypeData: trans.securityTypeData,
              securityPrincipalFactor: trans.securityPrincipalFactor,
              securityIndexFactor: trans.securityIndexFactor,
              decimalAccrued: trans.securityIndexFactor,
            } as Security
            this.currentSecurityId =
              trans.securityMasterData?.id ?? this.currentSecurityId
            this.security = security
            if (this.basisComponent) this.basisComponent.security = security
            if (this.accountDetails) {
              this.basisData = {
                tranType: this.originalTransaction.tranType,
                mvtMap: this.mvtDataMap
                  ? new Map(Object.entries<MvtData>(this.mvtDataMap))
                  : undefined,
                tpiMap: this.tpiMap
                  ? new Map(Object.entries<TransferPurchaseInfo>(this.tpiMap))
                  : undefined,
                accountDetails: this.accountDetails,
                reconDate: this.reconDate,
              } as BasisGridEdit

              this.genericData = {
                transaction: this.originalTransaction,
                security: security,
                accountDetails: this.accountDetails,
                reconDate: this.reconDate,
              } as GenericGridTransaction
            }
          },
          err => {
            this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
            this.errorDetails = err.error
          },
        )
    } else if (isExternalTransaction(this.transInput) && this.accountId) {
      this.title = 'Post Security Transfer'
      this.createEditTransactionService
        .loadExternalTransactionDetail(
          Number(this.transInput?.transactionId),
          this.accountId,
        )
        .subscribe(
          async externalTransactionDetail => {
            const externalTransactionData =
              externalTransactionDetail.externalTransaction
            this.accountDetails = externalTransactionDetail.accountDetails
            this.externalTranTypeLabel =
              externalTransactionData.transactionTypeLabel
            let currentAccountDetails = externalTransactionDetail.accountDetails

            if (
              externalTransactionDetail.externalTransaction.securityId &&
              externalTransactionDetail.externalTransaction.settleDate
            ) {
              // accountId should be set here; if it isn't, the backend treats the 0 as
              // "no account" and uses the global (clientId=1) security scope, not account 0.
              this.createEditTransactionService
                .fetchSecurityDetailsFromSecurityId(
                  externalTransactionDetail.externalTransaction.securityId,
                  externalTransactionDetail.externalTransaction.settleDate,
                  this.accountId ?? 0,
                )
                .subscribe(
                  async security => {
                    this.security = security
                    this.externalTransaction = externalTransactionData
                    this.executionId = externalTransactionData.executionId
                    this.externalAccountId = externalTransactionData.accountId
                    if (this.basisComponent)
                      this.basisComponent.security = security
                    if (currentAccountDetails) {
                      if (currentAccountDetails?.isAggregate) {
                        if (
                          currentAccountDetails.accountId !=
                          this.externalAccountId
                        ) {
                          // External post is for a concrete sub-account; resolve it
                          // directly so the fields stay unlocked and the account
                          // isn't cleared.
                          this.isExternalAccountSleeveSimple = true
                          await this.loadAndMapAccountDetailsForSleeve(
                            this.externalAccountId,
                          )
                          currentAccountDetails =
                            this.accountDetails ?? currentAccountDetails
                          this.aggregateAccountUnresolved = false
                          this.initialLoadDone = true
                        } else {
                          this.fetchAggInfo(currentAccountDetails.accountId)
                        }
                      }
                      this.basisData = {
                        transaction: externalTransactionData,
                        security: security,
                        accountDetails: currentAccountDetails,
                        reconDate: this.reconDate,
                      } as BasisGridExternal
                      this.genericData = {
                        transaction: externalTransactionData,
                        security: security,
                        accountDetails: currentAccountDetails,
                        reconDate: this.reconDate,
                      } as GenericGridTransaction
                    }
                  },
                  err => {
                    this.errorText = TransactionConstants.ERROR_LOADING_SECURITY
                    this.errorDetails = err.error
                  },
                )
            } else {
              this.setBasicDetails()
            }

            if (externalTransactionDetail.accountDetails) {
              this.defaultBasisId =
                externalTransactionDetail.accountDetails.defaultBasis ?? 1
            }
            this.originalCusip =
              externalTransactionData.cusip ??
              externalTransactionData.alternateId
          },
          err => {
            this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
            this.errorDetails = err.error
          },
        )
    } else if (
      this.transInput?.positionData &&
      'sessionReconDate' in this.transInput.positionData
    ) {
      const positionData = this.transInput.positionData
      try {
        const loadAccountDetails$ = this.createEditTransactionService
          .loadAccountDetails(positionData.accountId)
          .toPromise()
        const fetchSecurityDetails$ = this.createEditTransactionService
          .fetchSecurityDetailsFromSecurityId(
            positionData.securityId,
            positionData.sessionReconDate,
            positionData.accountId,
          )
          .toPromise()

        ;[this.accountDetails, this.security] = await Promise.all([
          loadAccountDetails$,
          fetchSecurityDetails$,
        ])
        if (this.accountDetails.isAggregate)
          this.fetchAggInfo(this.accountDetails.accountId)
      } catch (e) {
        this.errorText = TransactionConstants.ERROR_LOADING_SECURITY
        this.errorDetails = e.error
        console.error(
          'Error occurred while fetching security and accounts details',
        )
      }
      if (this.reconDate) {
        this.basisData = {
          accountDetails: this.accountDetails,
          reconDate: this.reconDate,
        } as SecurityGrid
        this.genericData = {
          transaction: {
            sessionReconDate: positionData.sessionReconDate,
            assetId: positionData.assetId,
            accountId: positionData.accountId,
            units: positionData.units,
            inventoryType: positionData.inventoryType,
            collateralBroker: positionData.collateralBroker,
          },
          security: this.security,
          reconDate: this.reconDate,
          accountDetails: this.accountDetails,
        }
      }
    } else {
      if (this.accountId) {
        this.errorDetails = ''
        this.createEditTransactionService
          .loadAccountDetails(this.accountId)
          .subscribe(
            accountDetails => {
              if (accountDetails) {
                this.accountDetails = accountDetails

                this.setBasicDetails()
                if (accountDetails.isAggregate) {
                  this.fetchAggInfo(accountDetails.accountId)
                }
              }
            },
            err => {
              this.errorHeader = TransactionConstants.ERROR_LOADING_ACCOUNT
              this.errorDetails = err.error
            },
          )
      }
    }

    const now = Date.now()
    const type = singleDialogMetricsDetail(this.transInput)
    this.telemetryService.emitCustomEvent(SINGLE_DIALOG_EVENT_NAME, {
      dialogName: 'Security-Transfer-' + type,
      durationMs: now - startTime,
    })
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  fetchAggInfo(accountId: number) {
    this.aggregateAccountUnresolved = true
    this.resetAggAccountDetails()
    this.fetchSleeveSimples(accountId)
    this.initialLoadDone = true
    this.validate()
  }

  resetAggAccountDetails() {
    this.accountId = undefined
    this.accountDetails = undefined
  }

  updateSleeveAccountsOnChange(sleeveAccountDetail: AccountDetails) {
    // A concrete sub-account is now resolved; unlock the child-grid fields.
    this.aggregateAccountUnresolved = false
    this.accountDetails = sleeveAccountDetail
    this.accountId = sleeveAccountDetail?.accountId
    this.setBasicDetails()
    if (isExternalTransaction(this.transInput)) {
      this.basisData = {
        transaction: this.externalTransaction,
        security: this.security,
        accountDetails: this.accountDetails,
        reconDate: this.reconDate,
      } as BasisGridExternal
      this.genericData = {
        transaction: this.externalTransaction,
        security: this.security,
        accountDetails: this.accountDetails,
        reconDate: this.reconDate,
      } as GenericGridTransaction
    }
  }

  fetchSleeveSimples(accountId: number) {
    const sleeveSimples$ = this.createEditTransactionService
      .fetchSubAccounts(accountId)
      .pipe(retry(2))
    sleeveSimples$.subscribe(
      account => {
        this.sleeveSimpleAccount = account
      },
      err => {
        this.errorHeader = TransactionConstants.ERROR_LOAD_SLEEVE_ACCOUNT
        this.errorDetails = err.error
      },
    )
  }

  setBasicDetails() {
    this.defaultBasisId = this.accountDetails?.defaultBasis ?? 1
    this.genericData = {
      accountDetails: this.accountDetails,
      reconDate: this.reconDate,
    } as SecurityGrid
    this.basisData = {
      accountDetails: this.accountDetails,
      reconDate: this.reconDate,
    } as SecurityGrid
  }

  displaySaveButtonWarning(warningMsg: string | undefined) {
    const modalRef = this.modalService.open(
      SecurityTransferSaveConfirmationModalComponent,
      { keyboard: false },
    )
    modalRef.componentInstance.saveModalInput = {
      warningMessage: warningMsg,
    }
    modalRef.result.then((confirmed: boolean) => {
      return confirmed ? this.save() : ''
    })
  }

  validateAndSave() {
    if (!this.sessionId) {
      return
    }
    return this.saveButtonWarning
      ? this.displaySaveButtonWarning(this.saveButtonWarning)
      : this.save()
  }

  save() {
    if (!this.sessionId) {
      return
    }
    this.store.dispatch(
      setCommandsMetrics({
        command: 'securityTransfer',
        time: Date.now(),
      }),
    )
    this.formSubmitting = true
    let unclearTransaction = false
    if (this.originalTransaction?.clearedDate && !this.clearTransaction.value) {
      unclearTransaction = true
    }
    const tpiMap = this.basisComponent
      ? this.basisComponent.getActiveTpis(
          this.genericComponent?.getUnits() ?? 0,
        )
      : {}
    const tranCommand: CreateEditTransactionRequest = {
      transaction: this.prepareTransactionData(
        false,
        tpiMap ? tpiMap[this.defaultBasisId] : undefined,
      ),
      tranCategory: TranCategory.TRANSFER,
      metadata: createMetadataSecurityTransfer(
        this.reconDate ?? new Date(),
        this.genericComponent?.getRowNode('cusip')?.data.value,
        Object.fromEntries(
          this.basisComponent
            ? this.basisComponent?.getSelectedMvtBases()
            : new Map<number, Basis>(),
        ),
        Object.fromEntries(
          this.basisComponent
            ? this.basisComponent?.getSelectedExternalMvtBases()
            : new Map<number, ExternalMvt>(),
        ),
        unclearTransaction,
        this.reprocessTransfers.value,
      ),
      mvts: this.basisComponent?.getActiveMvts(),
      tpis: Object.values(tpiMap),
    }
    if (this.note.value != '') {
      tranCommand.transactionNote = {
        newValue: this.note.value,
      }
    }
    if (this.originalTransaction?.transactionId) {
      tranCommand.notes =
        TransactionConstants.HELIOS_SECURITY_TRANSFER_SAVE_NOTE_CW_EDIT
      this.createEditTransactionService
        .editTransaction(
          this.sessionId,
          this.originalTransaction.transactionId,
          tranCommand,
        )
        .subscribe(
          () => {
            this.toastService.success(
              'Clearwater Transaction edit command successfully submitted.',
            )
            this.formSubmitting = false
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else {
      if (isExternalTransaction(this.transInput)) {
        tranCommand.notes =
          TransactionConstants.HELIOS_SECURITY_TRANSFER_SAVE_NOTE_EXTERNAL
        tranCommand.extTran = {
          externalAccountId: this.externalAccountId ?? 0,
          executionId: this.executionId ?? 0,
          externalTranId: Number(this.transInput?.transactionId),
          originalCusip: this.originalCusip,
          status: this.transInput?.externalTransactionStatus,
        }

        if (this.externalTranTypeLabel?.id) {
          tranCommand.transaction.typeLabel = {
            id: this.externalTranTypeLabel.id,
            label: this.externalTranTypeLabel.label,
            description: this.externalTranTypeLabel.description,
          }
        }

        this.createEditTransactionService
          .saveExternalMatch(this.sessionId, tranCommand)
          .subscribe(
            () => {
              this.toastService.success(
                'External Transaction command successfully submitted.',
              )
              this.formSubmitting = false
            },
            error => {
              this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
              this.errorDetails = error.error
              this.formSubmitting = false
            },
          )
      } else {
        tranCommand.notes =
          TransactionConstants.HELIOS_SECURITY_TRANSFER_SAVE_NOTE_CW_CREATE
        this.createEditTransactionService
          .createTransaction(this.sessionId, tranCommand)
          .subscribe(
            () => {
              this.toastService.success(
                'Clearwater Transaction create command successfully submitted.',
              )
              this.formSubmitting = false
            },
            error => {
              this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
              this.errorDetails = error.error
              this.formSubmitting = false
            },
          )
      }
    }
    this.formSubmitting = false
    this.closeModal()
  }

  transferDateSync(date: string) {
    this.basisComponent?.transferDateSync(date)
  }

  transferPriceSync(price: string) {
    this.basisComponent?.transferPriceSync(price)
  }

  validUnitChange(units: number) {
    this.basisComponent?.updateProceeds(units)
  }
  transferTypeChanged(transferOption: TransferOptions) {
    this.selectedTypeofTransfer = transferOption
    this.basisComponent?.setTransferTypeSelected(transferOption)
  }

  private prepareTransactionData(
    isValidate: boolean,
    tpiDefault?: TransferPurchaseInfo,
  ): SecurityTransfer {
    const collateralNode = this.genericComponent?.getRowNode('collateralBroker')
    let createdDate = new Date().toISOString()
    if (this.isEdit && this.originalTransaction?.createdDate) {
      createdDate = new Date(
        this.originalTransaction?.createdDate,
      ).toISOString()
    }

    const entryDate =
      this.genericComponent?.getRowNode('entryDate')?.data.value ??
      getStringISOForDate(new Date())
    const originalSettleDate =
      this.basisComponent?.getRowNode('origSettleDate')?.data.value
    let realSettle = entryDate
    if (originalSettleDate && entryDate) {
      realSettle = max([parseISO(entryDate), parseISO(originalSettleDate)])
        .toISOString()
        .split('T')[0]
    }
    this.lastUsedSettleDate = realSettle
    const units = this.genericComponent?.getUnits()
    const transactionData: SecurityTransfer = {
      clientLotId:
        this.genericComponent?.getRowNode('clientLotId')?.data.value ?? null,
      inventoryType:
        this.genericComponent?.getRowNode('inventoryType')?.data.value,
      postDate: this.getPostDate(
        this.selectedTypeofTransfer.tranType,
        realSettle,
      ),
      amount: this.basisComponent?.getRowNode('proceeds')?.data.value ?? 0,
      transferPrice: Number(
        this.genericComponent?.getRowNode('transferPrice')?.data.value,
      ),
      units: units ?? 0,
      counterPartyId: this.originalTransaction?.counterPartyId ?? 13,
      counterParty: getDefaultUnknownCounterParty(),
      tranType: this.selectedTypeofTransfer.tranType,
      createdDate: createdDate,
      active: true,
      userId: this.userId ?? 0,
      sourceId: TransactionConstants.HELIOS_SOURCE_ID,
      accountId: this.accountId ?? 0,
      entryDate: entryDate,
      settleDate: this.getSettleDate(
        this.selectedTypeofTransfer.tranType,
        realSettle,
      ),
      modifiedDate: new Date().toISOString(),
      dataMap: {
        collateralBroker: collateralNode?.data.value ?? null,
        clientLotId:
          this.genericComponent?.getRowNode('clientLotId')?.data.value ?? null,
        prepaidInterest:
          this.basisComponent?.getRowNode('prepaidInterest')?.data.default ??
          null,
        interestDue:
          this.basisComponent?.getRowNode('interestDue')?.data.default ?? null,
        interestBearingInterestReserve:
          this.basisComponent?.getRowNode('interestBearingInterestReserve')
            ?.data.default ?? null,
        interestBearingPrincipalReserve:
          this.basisComponent?.getRowNode('interestBearingPrincipalReserve')
            ?.data.default ?? null,
        nonInterestBearingInterestReserve:
          this.basisComponent?.getRowNode('nonInterestBearingInterestReserve')
            ?.data.default ?? null,
        nonInterestBearingPrincipalReserve:
          this.basisComponent?.getRowNode('nonInterestBearingPrincipalReserve')
            ?.data.default ?? null,
      },
      traderId: 0,
      revisionId: this.isEdit
        ? (this.originalTransaction?.revisionId ?? 0) + 1
        : 1,
    }

    if (this.originalTransaction?.basisId) {
      transactionData.basisId = this.originalTransaction?.basisId
    }

    if (tpiDefault?.originalOriginationFee) {
      transactionData.dataMap = Object.assign(transactionData.dataMap ?? {}, {
        originationFee: tpiDefault?.originalOriginationFee,
      })
    }
    if (tpiDefault?.exitFee) {
      transactionData.dataMap = Object.assign(transactionData.dataMap ?? {}, {
        exitFee: tpiDefault?.exitFee,
      })
    }
    if (tpiDefault?.exitFeeReceivable) {
      transactionData.dataMap = Object.assign(transactionData.dataMap ?? {}, {
        exitFeeReceivable: tpiDefault?.exitFeeReceivable,
      })
    }
    if (tpiDefault?.drawFee) {
      transactionData.dataMap = Object.assign(transactionData.dataMap ?? {}, {
        drawFee: tpiDefault?.drawFee,
      })
    }
    if (tpiDefault?.prepaymentPenaltyFee) {
      transactionData.dataMap = Object.assign(transactionData.dataMap ?? {}, {
        prepaymentPenaltyFee: tpiDefault?.prepaymentPenaltyFee,
      })
    }
    if (tpiDefault?.prepaidInterest) {
      transactionData.dataMap = Object.assign(transactionData.dataMap ?? {}, {
        prepaidInterest: tpiDefault?.prepaidInterest,
      })
    }
    if (tpiDefault?.interestDue) {
      transactionData.dataMap = Object.assign(transactionData.dataMap ?? {}, {
        interestDue: tpiDefault?.interestDue,
      })
    }
    transactionData.dataMap = Object.assign(
      transactionData.dataMap ?? {},
      getLoanFieldValues(field => tpiDefault?.[field]),
    )

    if (!isValidate) {
      transactionData.securityId = this.currentSecurityId
      if (this.isEdit) {
        transactionData.oldClientLotId = transactionData.clientLotId
      }
    }
    if (
      ENTRIES.includes(
        TransactionTypeEnum[this.selectedTypeofTransfer.tranType],
      )
    ) {
      const proceeds = this.calculateProceedsForTransferIn(
        transactionData.settleDate,
        transactionData.entryDate,
        Number(this.genericComponent?.getRowNode('units')?.data.value),
        tpiDefault,
      )
      transactionData.amount = proceeds
    }
    if (this.clearTransaction.value) {
      transactionData.clearedDate = isValidate
        ? new Date().toISOString().split('Z')[0]
        : new Date().toISOString()
    }
    if (this.originalTransaction != null) {
      transactionData.transactionId = this.originalTransaction.transactionId
      transactionData.legacyOrigTranId =
        this.originalTransaction.legacyOrigTranId
      transactionData.legacyTranId = this.originalTransaction.legacyTranId
    }
    if (!isValidate && this.originalTransaction?.typeLabelId) {
      transactionData.typeLabelId = this.originalTransaction.typeLabelId
    }
    return transactionData
  }

  getPostDate(tranType: TransactionTypeKey, realSettle: any): string {
    let postDate = realSettle
    if (tranType == 'TRANSFER_IN' || tranType == 'INITIAL_LOAD') {
      postDate =
        compareAsc(
          parseISO(realSettle),
          parseISO(
            this.basisComponent?.getRowNode('origSettleDate')?.data.default,
          ),
        ) === -1
          ? this.basisComponent?.getRowNode('origSettleDate')?.data.default
          : realSettle
    }
    return postDate
  }

  getSettleDate(tranType: TransactionTypeKey, realSettle: any): string {
    let settleDate = realSettle
    if (tranType == 'TRANSFER_IN' || tranType == 'INITIAL_LOAD')
      settleDate =
        this.basisComponent?.getRowNode('origSettleDate')?.data?.default
    return settleDate == '' ? realSettle : settleDate
  }

  calculateProceedsForTransferIn(
    settleDate: string,
    entryDate: string,
    units: number,
    tpi?: TransferPurchaseInfo,
  ): number {
    if (!this.isCashAffecting(settleDate, entryDate)) return 0

    if (!tpi) return 0

    const price = tpi.origPrice ?? 0

    const accrued = tpi.origAccruedInt ?? 0
    const commission = tpi.commission
    const exitFee = tpi.exitFee ?? 0
    const exitFeeReceivable = tpi.exitFeeReceivable ?? 0
    const prepaidInterest = tpi.prepaidInterest ?? 0.0
    const interestDue = tpi.interestDue ?? 0.0
    const reserveTotal =
      (tpi.interestBearingInterestReserve ?? 0.0) +
      (tpi.interestBearingPrincipalReserve ?? 0.0) +
      (tpi.nonInterestBearingInterestReserve ?? 0.0) +
      (tpi.nonInterestBearingPrincipalReserve ?? 0.0)
    const tranType = TransactionTypeEnum[this.selectedTypeofTransfer.tranType]
    const inventoryType =
      this.genericComponent?.getRowNode('inventoryType')?.data.value

    return this.security
      ? Calculators.calculateProceeds(
          price,
          accrued,
          units,
          commission,
          0.0, //Origination Fees are not expected for proceeds on a security transfer
          0.0, //Draw Fees are not expected for proceeds on a security transfer
          exitFee,
          exitFeeReceivable,
          0.0, //Prepayment Penalty Fees are not expected for proceeds on a security transfer
          prepaidInterest,
          interestDue,
          reserveTotal,
          this.security,
          tranType,
          tpi.factor,
          this.applyHPL,
          inventoryType,
        )
      : 0
  }

  isCashAffecting(settleDate: string, entryDate: string): boolean {
    const cashTypes = [
      TransactionTypeEnum.CAPITALIZATION_OF_INTEREST,
      TransactionTypeEnum.INITIAL_LOAD_OF_DEFERRED,
      TransactionTypeEnum.STOCK_SPLIT,
      TransactionTypeEnum.EXPIRATION,
      TransactionTypeEnum.WRITE_DOWN,
    ]
    if (
      cashTypes.includes(
        TransactionTypeEnum[this.selectedTypeofTransfer.tranType],
      )
    )
      return false

    return (
      (TransactionTypeEnum[this.selectedTypeofTransfer.tranType] ==
        TransactionTypeEnum.TRANSFER_IN ||
        TransactionTypeEnum[this.selectedTypeofTransfer.tranType] ==
          TransactionTypeEnum.INITIAL_LOAD) &&
      isAfter(parseISO(settleDate), parseISO(entryDate))
    )
  }

  calculatePrice(event: CellValueChangedEvent) {
    const basisField = event.colDef.field ?? 'default'
    const cost = Number(event.value)
    this.calculateBasisPriceByBasisFieldName(basisField, event.data.id, cost)
  }

  pricesChangeByUnit() {
    this.calculatePriceByUnits('amortStartCost')
    this.calculatePriceByUnits('origCost')
  }

  calculatePriceByUnits(costField: string) {
    const basisTypes = this.basisComponent?.basisTypes ?? commonBasisTypes
    const overrideForBasis = this.basisComponent?.rowData.find(
      element => element.id == 'overrideForBasis',
    )

    basisTypes.forEach(basis => {
      const basisField =
        basis.prefix == '' ? 'default' : basis.prefix.toLowerCase()
      let cost = Number(
        this.basisComponent?.getRowNode(costField)?.data[basisField],
      )
      if (this.basisComponent?.defaultBasisId == basis.basisId) {
        cost = Number(
          this.basisComponent?.getRowNode(costField)?.data['default'],
        )
        this.calculateBasisPriceByBasisFieldName('default', costField, cost)
      }
      if (overrideForBasis[basisField]) {
        this.calculateBasisPriceByBasisFieldName(basisField, costField, cost)
      }
    })
  }

  calculateBasisPriceByBasisFieldName(
    basisField: string,
    costField: string,
    cost: number,
  ) {
    if ('amortStartCost' == costField) {
      costField = 'amortStartPrice'
    } else if ('origCost' == costField) {
      costField = 'origPrice'
    }
    const factor = this.security?.securityPrincipalFactor?.factor ?? 0
    const units = Number(this.genericComponent?.getRowNode('units')?.data.value)
    if (cost && this.security && units && factor) {
      this.basisComponent
        ?.getRowNode(costField)
        ?.setDataValue(
          basisField,
          calculateBasisPrice(
            cost,
            factor * units,
            this.security.securityMasterData.priceScalar ?? 0.01,
            this.security.securityMasterData.contractSize ?? 1.0,
          ) ?? 0,
        )
    }
  }

  validate() {
    const startTime = Date.now()
    if (!this.initialLoadDone) return
    const categoryNode = this.genericComponent?.getRowNode('tranType')
    if (!categoryNode?.data.value) {
      return
    }
    // Don't validate while an aggregate account is unresolved; there's no concrete
    // account to scope the request to yet (edited fields are flagged instead).
    if (this.aggregateAccountUnresolved) {
      return
    }
    this.isDataValid = false

    const validateView = this.createValidationView(
      this.accountDetails as AccountDetails,
    )
    this.isValidating = true
    this.createEditTransactionService
      .validateTransaction(validateView, TranCategoryUrl.TRANSFER)
      .subscribe(
        validationInfos => {
          if (
            this.isCollateralBrokerAccount &&
            !(validateView.userData.transaction as SecurityTransfer).dataMap
              ?.collateralBroker
          ) {
            validationInfos.push({
              message: 'Collateral Broker must be specified',
              level: ErrorSeverity.ERROR,
              impactedFields: ['collateralBroker'],
            })
          }
          this.handleValidationResponse(
            validationInfos.filter(
              validationInfo =>
                validationInfo.level != ErrorSeverity.FORBIDDEN &&
                !validationInfo.impactedFields.includes('generic'),
            ),
          )
          this.handleValidationResponseForbidden(
            validationInfos.filter(
              validationInfo => validationInfo.level == ErrorSeverity.FORBIDDEN,
            ),
          )
          if (!this.forbiddenErrors.errors) {
            this.handleValidationResponseGeneric(
              validationInfos.filter(validationInfo =>
                validationInfo.impactedFields.includes('generic'),
              ),
            )
            this.handleValidationResponseConfirmButton(
              validationInfos.filter(validationInfo =>
                validationInfo.impactedFields.includes('confirmButton'),
              ),
            )
          }
          this.isValidating = false
          const now = Date.now()
          const type = singleDialogMetricsDetail(this.transInput)
          this.telemetryService.emitCustomEvent(
            SINGLE_DIALOG_VALIDATION_EVENT_NAME,
            {
              dialogName: 'Security-Transfer-' + type,
              durationMs: now - startTime,
            },
          )
        },
        err => {
          this.errorHeader = TransactionConstants.VALIDATION_ERROR
          this.errorDetails = err.error
          this.isValidating = false
        },
      )
  }

  handleValidationResponseForbidden(validationInfos: ValidationInfo[]) {
    if (validationInfos.length == 0) return
    const forbiddenErrorsList: string[] = []
    validationInfos.forEach(validationInfo => {
      forbiddenErrorsList.push(validationInfo.message)
    })
    const validatorKey = 'forbiddenErrorsValidator'
    this.forbiddenErrors.markAsTouched()
    this.forbiddenErrors.setErrors({
      [validatorKey]: {
        message: forbiddenErrorsList,
      },
    })
    this.isDataValid = false
  }

  handleValidationResponseGeneric(genericValidationInfos: ValidationInfo[]) {
    if (this.transInput?.transactionBasis) {
      this.genericWarnings.add(
        TransactionConstants.BASIS_TRANSFER_EDIT_WARNING.replace(
          '[BASIS]',
          this.transInput?.transactionBasis,
        ),
      )
    }
    genericValidationInfos.forEach(validationInfo => {
      this.genericWarnings.add(validationInfo.message)
    })
  }

  handleValidationResponseConfirmButton(validationInfos: ValidationInfo[]) {
    if (validationInfos.length == 0) return
    const saveButtonWarning: string[] = []
    validationInfos.forEach(validationInfo => {
      saveButtonWarning.push(validationInfo.message)
    })
    this.saveButtonWarning = saveButtonWarning.join()
  }

  handleValidationResponse(validationInfos: ValidationInfo[]) {
    const errorMessages: Map<string, string[]> = new Map()
    const warningMessages: Map<string, string[]> = new Map()
    validationInfos.forEach(validationInfo => {
      if (validationInfo.level == ErrorSeverity.ERROR) {
        validationInfo.impactedFields.forEach(impactedField => {
          if (errorMessages.has(impactedField)) {
            errorMessages.get(impactedField)?.push(validationInfo.message)
          } else {
            errorMessages.set(impactedField, [validationInfo.message])
          }
        })
      } else {
        validationInfo.impactedFields.forEach(impactedField => {
          if (warningMessages.has(impactedField)) {
            warningMessages.get(impactedField)?.push(validationInfo.message)
          } else {
            warningMessages.set(impactedField, [validationInfo.message])
          }
        })
      }
    })
    this.isDataValid = errorMessages.size == 0
    this.basisComponent?.markAllNodeValid()
    this.genericComponent?.markAllNodeValid()
    this.note.setErrors(null)
    errorMessages.forEach((value: string[], key: string) => {
      let currentNode =
        this.genericComponent?.getRowNode(key) ??
        this.basisComponent?.getRowNode(key)
      let component = this.genericComponent?.getRowNode(key)
        ? this.genericComponent
        : this.basisComponent
      if (key == 'securityId') {
        currentNode = this.genericComponent?.getRowNode('cusip')
        component = this.genericComponent
      }
      if (key == 'accountId') {
        currentNode = this.genericComponent?.getRowNode('account')
        component = this.genericComponent
      }
      if (key == 'settleDate') {
        const entryDate = this.genericComponent?.getRowNode('entryDate')
        if (entryDate?.data.value == this.lastUsedSettleDate) {
          currentNode = entryDate
          component = this.genericComponent
        } else {
          currentNode = this.basisComponent?.getRowNode('origSettleDate')
          component = this.basisComponent
        }
      }
      if (currentNode) {
        component?.handleValidation(currentNode, 'INVALID', value.join(' | '))
      } else if (key == 'transactionNote') {
        const validatorKey = 'transactionNoteValidator'
        this.note.markAsTouched()
        this.note.setErrors({
          [validatorKey]: {
            message: value,
          },
        })
      }
    })
    warningMessages.forEach((value: string[], key: string) => {
      let currentNode =
        this.genericComponent?.getRowNode(key) ??
        this.basisComponent?.getRowNode(key)
      let component = this.genericComponent?.getRowNode(key)
        ? this.genericComponent
        : this.basisComponent
      if (key == 'securityId') {
        currentNode = this.genericComponent?.getRowNode('cusip')
        component = this.genericComponent
      }
      if (key == 'accountId') {
        currentNode = this.genericComponent?.getRowNode('account')
        component = this.genericComponent
      }
      if (key == 'settleDate') {
        const entryDate = this.genericComponent?.getRowNode('entryDate')
        if (entryDate?.data.value == this.lastUsedSettleDate) {
          currentNode = entryDate
          component = this.genericComponent
        } else {
          currentNode = this.basisComponent?.getRowNode('origSettleDate')
          component = this.basisComponent
        }
      }
      if (currentNode && currentNode.data.errorMessage == '') {
        component?.handleValidation(currentNode, 'WARNING', value.join(' | '))
      } else if (
        key == 'transactionNote' &&
        !errorMessages.has('transactionNote')
      ) {
        const validatorKey = 'transactionNoteValidator'
        this.noteWarning = {
          [validatorKey]: {
            message: value,
          },
        }
      }
    })
    if (this.basisComponent?.getRowNode('origSettleDate')?.data.errorMessage)
      this.origSettleDateValidationError = true
    else this.origSettleDateValidationError = false
  }

  createValidationView(
    accountDetails: AccountDetails,
  ): SecurityTransferValidationView {
    const view: SecurityTransferValidationView = {
      userData: {
        transaction: this.prepareTransactionData(true),
        cusip: this.genericComponent?.getRowNode('cusip')?.data.value,
        existingUnits:
          this.originalTransaction?.units != null
            ? this.originalTransaction.units
            : 0,
      },
      sessionId: this.sessionId ?? '',
      transactionBasis: this.transInput?.transactionBasis,
      accountDetails: accountDetails,
      requestMode: getRequestModeFromModalMode(this.transInput),
      windowDate: this.windowDate ?? new Date(),
      reconDate: this.reconDate ?? new Date(),
      basisTransactionData: Object.fromEntries(
        this.basisComponent
          ? this.basisComponent.createBasisTransactionData()
          : new Map<string, ValidationBasisData>(),
      ),
    }
    if (this.note.value != '') {
      view.transactionNote = this.note.value
    }
    if (this.externalAccountId) {
      view.externalTransaction = {
        accountId: this.externalAccountId,
        isSleeveSimple: this.isExternalAccountSleeveSimple,
      } as ValidationExternalTransactionData
    }
    return view
  }

  securityDetailsUpdated(securityDetails: Security) {
    this.security = securityDetails
    if (this.basisComponent) this.basisComponent.security = securityDetails
    this.currentSecurityId = securityDetails.securityMasterData.id
    if (isSecurityNonCommission(securityDetails)) {
      this.basisComponent?.nonComissionSecuritySelected()
    }
  }

  allLoadedGeneric() {
    this.genericLoaded = true
    if (this.basisLoaded) {
      this.initialLoadDone = true
      this.validate()
    }
  }

  allLoadedBasis() {
    this.basisLoaded = true
    if (this.genericLoaded) {
      this.initialLoadDone = true
      this.validate()
    }
  }

  private closeModal() {
    this.activeModal.close()
  }

  disableGridFocus() {
    this.basisComponent?.disableGridFocus()
    this.genericComponent?.disableGridFocus()
  }

  async loadAndMapAccountDetailsForSleeve(accountId: number) {
    this.accountDetails = await this.createEditTransactionService
      .loadAccountDetails(accountId)
      .toPromise()
    this.accountId = this.accountDetails?.accountId
  }

  editingStarted() {
    this.isCellEditing = true
  }

  editingStopped() {
    this.isCellEditing = false
  }
  invalidateData() {
    this.isDataValid = false
  }

  @HostListener('click') dialogClicked() {
    if (isDialogComponentInActive(this.compId)) {
      activateDialog(this.activeInstances, this.compId, this.elRef)
    }
  }

  handleGridFocusChange(gridName: string) {
    if (gridName === 'basis') {
      // Clear selection generic grid
      this.genericComponent?.gridApi?.api.clearRangeSelection()
    } else if (gridName === 'generic') {
      // Clear selection for basis grid
      this.basisComponent?.gridApi?.api.clearRangeSelection()
    }
  }

  getComponentId() {
    return this.compId
  }

  extractBasisIdAndSettleDate(map: Map<string, ValidationBasisData>) {
    const basisSettleDateMap: { [key: string]: string } = {}
    map.forEach(value => {
      basisSettleDateMap[value.basisId.toString()] = value.origSettleDate
    })
    return { basisSettleDateMap }
  }

  useCalculatedFactor() {
    const isSettleDateValidationError =
      !!this.basisComponent?.getRowNode('origSettleDate')?.data.errorMessage
    const securityId = this.security?.securityMasterData.id
    const getFactorRequest = this.extractBasisIdAndSettleDate(
      this.basisComponent?.createBasisTransactionData() ?? new Map(),
    )
    const columnBasis: { basisId: number; prefix: string }[] =
      this.basisComponent?.basisTypes?.map(basis => ({
        basisId: basis.basisId,
        prefix: basis.prefix,
      })) ?? []

    if (securityId && !isSettleDateValidationError) {
      this.isCalculatingFactor = true
      // accountId should be set here; if it isn't, the backend treats the 0 as
      // "no account" and uses the global (clientId=1) security scope, not account 0.
      this.createEditTransactionService
        .getCalculatedFactor(securityId, getFactorRequest, this.accountId ?? 0)
        .subscribe(
          value => {
            const rowNode = this.basisComponent?.getRowNode('factor')?.data
            let hasChanged = false // Flag to track changes
            Object.entries(value).forEach(([basisId, factor]) => {
              const basisPrefix =
                Number(basisId) == this.defaultBasisId
                  ? 'default'
                  : columnBasis
                      .find(value1 => value1.basisId == Number(basisId))
                      ?.prefix.toLowerCase() ?? ''

              if (!rowNode[basisPrefix]) {
                rowNode[basisPrefix] = factor
                hasChanged = true
              }
            })
            if (hasChanged) {
              // Only refresh and validate if changes occurred
              this.basisComponent?.gridApi?.api.refreshCells()
              this.validate()
            }
            this.isCalculatingFactor = false
          },
          err => {
            this.isCalculatingFactor = false
            this.errorHeader =
              TransactionConstants.ERROR_LOADING_CALCULATED_FACTOR
            this.errorDetails = err.error
          },
        )
    }
  }

  requestPrice() {
    const modalRef = this.modalService.open(RequestPriceComponent, {
      size: 'md',
      windowClass: 'modal-window-click-through',
      keyboard: false,
    } as NgbModalOptions)
    modalRef.componentInstance.assetId =
      this.genericComponent?.getRowNode('cusip')?.data.value
    modalRef.componentInstance.priceNode =
      this.genericComponent?.getRowNode('transferPrice')
  }

  setErrorDetails(errorDetails: unknown) {
    this.errorDetails = errorDetails
  }

  setErrorHeader(errorHeader: string | undefined) {
    this.errorHeader = errorHeader
  }
}
